<script setup>
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import formTypeService from '@/services/formType.service'
import FormTypeDrawer from '@/views/feedback/master/FormTypeDrawer.vue'
import { VDataTableServer } from 'vuetify/labs/VDataTable'

const searchQuery = ref('')
const debouncedSearch = refDebounced(searchQuery, 500)
const isConfirmDialogVisible = ref(false)

definePage({
  meta: {
    name: 'master-form-type',
    subject: 'feedback.menu',
    action: 'master_form_type',
  },
})

// Data table options
const itemsPerPage = ref(10)
const page = ref(1)
const sortBy = ref()
const orderBy = ref()

const errors = ref({
  name: [],
  code: [],
  status: [],
})

const onError = error => {
  if (error.data){
    errors.value = error.data.errors
  }
}

const updateOptions = options => {
  page.value = options.page
  orderBy.value = options.sortBy[0]?.key
  sortBy.value = options.sortBy[0]?.order
}

// Headers
const headers = [
  {
    title: 'Name',
    key: 'name',
  },
  {
    title: 'Code',
    key: 'code',
  },
  {
    title: 'Actions',
    key: 'actions',
    sortable: false,
  },
]

const {
  data: formTypesData,
  execute: fetchFormTypes,
  isFetching: isFetchingFormTypes,
} = await useApi(createUrl('/masters/form-types', {
  query: {
    paged: 1,
    q: debouncedSearch,
    itemsPerPage,
    page,
    sort: sortBy,
    order_by: orderBy,
  },
}))

const formTypes = computed(() => formTypesData.value?.data)
const totalFormTypes = computed(() => formTypesData.value?.meta?.total)

const isFormTypeDrawerVisible = ref(false)
const formTypeDrawerData = ref()
const formTypeDeleteData = ref()
const formTypeDrawerType = ref('Add')

const updateFormType = data => {
  if (data.id){
    var isUpdated = false
    Object.keys(data).forEach(key => {
      if (formTypeDrawerData.value[key] !== data[key]){
        isUpdated = true
      }
    })
  }

  if (!isUpdated){
    isFormTypeDrawerVisible.value = false

    return
  }
  formTypeService.updateFormType(data.id, data, () => {
    fetchFormTypes()
    isFormTypeDrawerVisible.value = false
    resetErrors()
  }, onError)
}

const saveFormType = data => {
  formTypeService.addFormType(data, () => {
    fetchFormTypes()
    isFormTypeDrawerVisible.value = false
    resetErrors()
  }, onError)
}

const resetErrors = () => {
  errors.value = {
    name: [],
    code: [],
    status: [],
  }
}

const showFormType = (formTypeData, type) => {
  formTypeDrawerData.value = formTypeData ? { ...formTypeData } : null
  formTypeDrawerType.value = type
  isFormTypeDrawerVisible.value = true
}

const deleteFormType = async (confirm, data) => {
  if(data?.id && confirm ){
    await formTypeService.deleteFormType(data.id, () => {
      fetchFormTypes()
      isConfirmDialogVisible.value = false
    }, onError)
  }
}

const showConfirmDialog = formTypeData => {
  formTypeDeleteData.value = formTypeData
  isConfirmDialogVisible.value = true
}

watch(isConfirmDialogVisible, () => {
  if (!isConfirmDialogVisible.value){
    formTypeDeleteData.value = {}
  }
})
</script>

<template>
  <section>
    <VCard
      variant="flat"
      class="border"
    >
      <VCardText class="d-flex flex-wrap py-4 gap-4">
        <!-- 👉 Left side: Filters -->
        <div class="d-flex align-center flex-wrap gap-4 filter-section">
          <div class="filter-field filter-field--search">
            <AppTextField
              v-model="searchQuery"
              placeholder="Search"
              density="compact"
            />
          </div>
        </div>

        <VSpacer />

        <!-- 👉 Right side: Action Buttons -->
        <div class="d-flex align-center flex-wrap gap-4">
          <VBtn
            v-if="$can('create', 'master.form_type')"
            prepend-icon="tabler-plus"
            variant="flat"
            @click="showFormType(null, 'Add')"
          >
            Add Form Type
          </VBtn>

          <IconBtn
            variant="tonal"
            class="rounded"
            :loading="isFetchingFormTypes"
            @click="fetchFormTypes"
          >
            <VIcon
              size="24"
              icon="tabler-refresh"
            />
          </IconBtn>
        </div>
      </VCardText>

      <VDivider />

      <VDataTableServer
        v-model:items-per-page="itemsPerPage"
        v-model:page="page"
        :items="formTypes"
        :items-length="totalFormTypes"
        :headers="headers"
        :loading="isFetchingFormTypes"
        class="text-no-wrap"
        @update:options="updateOptions"
      >
        <template #item.name="{ item }">
          <span
            class="text-base font-weight-medium cursor-pointer"
            @click="showFormType(item, 'View')"
          >
            {{ item.name }}
          </span>
        </template>

        <template #item.code="{ item }">
          <span class="text-capitalize font-weight-medium">{{ item.code }}</span>
        </template>

        <!-- Actions -->
        <template #item.actions="{ item }">
          <IconBtn
            v-if="$can('delete', 'master.form_type')"
            @click="showConfirmDialog(item)"
          >
            <VIcon icon="tabler-trash" />
          </IconBtn>

          <IconBtn
            v-if="$can('update', 'master.form_type')"
            @click="showFormType(item, 'Edit')"
          >
            <VIcon icon="tabler-edit" />
          </IconBtn>
        </template>
      </VDataTableServer>
    </VCard>
    <FormTypeDrawer
      v-model:isDrawerOpen="isFormTypeDrawerVisible"
      :form-type-data="formTypeDrawerData"
      :type="formTypeDrawerType ?? 'Add'"
      :errors="errors"
      @update:type="formTypeDrawerType = $event"
      @update:form-type-data="updateFormType"
      @save-form-type="saveFormType"
    />
    <ConfirmDialog
      v-model:isDialogVisible="isConfirmDialogVisible"
      confirmation-question="Are you sure you want to delete this form type?"
      confirm-msg="Form type has been deleted successfully."
      confirm-title="Form Type Deleted!"
      :data="formTypeDeleteData"
      @confirm="deleteFormType"
    />
  </section>
</template>

<style scoped>
.filter-field {
  inline-size: 15rem;
}

.filter-field--search {
  inline-size: 15rem;
}

@media (max-width: 600px) {
  .filter-section {
    inline-size: 100%;
  }

  .filter-field,
  .filter-field--search {
    inline-size: 100%;
  }
}

:deep(.v-data-table-footer) {
  border-block-start: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
  margin-block-start: 0 !important;
  padding-block: 0.5rem;
}
</style>
